import { VisionEvent } from '../../api/client';

const STALE_MS = 10 * 60 * 1000;

function capturedLabel(capturedAt: string): string {
  const d = new Date(capturedAt);
  return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}:${d.getSeconds().toString().padStart(2, '0')}`;
}

function isStale(capturedAt: string | undefined): boolean {
  if (!capturedAt) return true;
  return Date.now() - new Date(capturedAt).getTime() > STALE_MS;
}

const ROAST_LEVELS = [
  { label: 'Coffee 1',  color: '#748060', score: 95 },
  { label: 'Coffee 2',  color: '#917D60', score: 85 },
  { label: 'Coffee 3',  color: '#846A52', score: 75 },
  { label: 'Coffee 4',  color: '#745642', score: 65 },
  { label: 'Coffee 5',  color: '#5E4231', score: 55 },
  { label: 'Coffee 6',  color: '#483227', score: 45 },
  { label: 'Coffee 7',  color: '#332621', score: 35 },
  { label: 'Coffee 8',  color: '#221D1B', score: 25 },
  { label: 'Coffee 9',  color: '#1B1817', score: 15 },
  { label: 'Coffee 10', color: '#141414', score:  5 },
];

const ROAST_LEVELS_BY_SCORE = [...ROAST_LEVELS].sort((a, b) => a.score - b.score);

interface Props {
  visionEvent: VisionEvent | null;
  targetRoastScore?: number | null;
}

export default function RoastScaleDisplay({ visionEvent, targetRoastScore }: Props) {
  const currentScore = visionEvent?.roast_level_score;
  const stale = isStale(visionEvent?.captured_at);

  if (stale) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-1 text-center">
        <span className="text-gray-300 text-2xl">📊</span>
        <span className="text-gray-400 text-sm">ממתין לנתוני ויז׳ן...</span>
        {visionEvent?.captured_at && (
          <span className="text-xs text-gray-300">עודכן לאחרונה: {capturedLabel(visionEvent.captured_at)}</span>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full gap-2 px-2">
      {/* Color gradient bar */}
      <div className="relative flex-1 rounded-lg overflow-hidden border border-gray-300 min-h-8">
        {/* Gradient background */}
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(to right, ${ROAST_LEVELS_BY_SCORE.map((l) => l.color).join(', ')})`,
          }}
        />

        {/* Level labels */}
        <div className="absolute inset-0 flex">
          {ROAST_LEVELS_BY_SCORE.map((level, i) => (
            <div key={i} className="flex-1 flex items-end justify-center pb-1">
              <span className="text-[8px] text-white/70 font-medium truncate px-0.5" style={{ textShadow: '0 0 3px #000' }}>
                {level.label}
              </span>
            </div>
          ))}
        </div>

        {/* Target marker */}
        {targetRoastScore != null && (
          <div
            className="absolute top-0 bottom-0 flex flex-col items-center"
            style={{ left: `${targetRoastScore}%`, transform: 'translateX(-50%)' }}
          >
            <div className="w-0.5 h-full bg-amber-400 opacity-90" />
            <div className="absolute -top-0 -translate-x-1/2 left-1/2">
              <div className="w-3 h-3 bg-amber-400 rotate-45 border border-white" />
            </div>
          </div>
        )}

        {/* Current position indicator */}
        {currentScore != null && (
          <div
            className="absolute top-0 bottom-0 flex flex-col items-center z-10 transition-all duration-1000"
            style={{ left: `${currentScore}%`, transform: 'translateX(-50%)' }}
          >
            <div className="w-1 h-full bg-white opacity-90 shadow-lg" />
            <div className="absolute -top-0 -translate-x-1/2 left-1/2">
              <div className="w-3 h-3 bg-white rounded-full border-2 border-gray-600 shadow" />
            </div>
          </div>
        )}
      </div>

      {/* Score readout */}
      <div className="flex items-center justify-between text-sm" dir="rtl">
        <div className="text-center">
          <div className="text-xl font-extrabold text-gray-800">
            {currentScore != null ? Math.round(currentScore) : '—'}
          </div>
          <div className="text-xs text-gray-500">נוכחי</div>
          {visionEvent?.roast_level_label && (
            <div className="text-xs font-semibold text-amber-700">{visionEvent.roast_level_label}</div>
          )}
        </div>
        <div className="text-gray-300 text-lg">→</div>
        <div className="text-center">
          <div className="text-xl font-extrabold text-amber-700">
            {targetRoastScore != null ? Math.round(targetRoastScore) : '—'}
          </div>
          <div className="text-xs text-gray-500">יעד</div>
          {targetRoastScore != null && (
            <div className="text-xs font-semibold text-amber-600">
              {ROAST_LEVELS.reduce((best, l) => Math.abs(l.score - targetRoastScore) < Math.abs(best.score - targetRoastScore) ? l : best).label}
            </div>
          )}
        </div>
      </div>

      {/* Color swatches */}
      <div className="flex items-center justify-around" dir="rtl">
        <div className="flex flex-col items-center gap-1">
          <div
            className="w-10 h-10 rounded-xl border border-gray-300 shadow"
            style={
              visionEvent?.average_rgb
                ? { backgroundColor: `rgb(${visionEvent.average_rgb[0]}, ${visionEvent.average_rgb[1]}, ${visionEvent.average_rgb[2]})` }
                : { backgroundColor: '#e5e7eb' }
            }
          />
          <span className="text-xs text-gray-500">נוכחי</span>
        </div>
        <div className="flex flex-col items-center gap-1">
          <div
            className="w-10 h-10 rounded-xl border border-gray-300 shadow"
            style={
              visionEvent?.predicted_cooled_rgb
                ? { backgroundColor: `rgb(${visionEvent.predicted_cooled_rgb[0]}, ${visionEvent.predicted_cooled_rgb[1]}, ${visionEvent.predicted_cooled_rgb[2]})` }
                : { backgroundColor: '#e5e7eb' }
            }
          />
          <span className="text-xs text-gray-500">צפי</span>
        </div>
        {visionEvent?.target_distance != null && (
          <div className="text-center">
            <div className={`text-sm font-bold ${visionEvent.target_distance <= 18 ? 'text-green-600' : 'text-gray-500'}`}>
              {Math.round(visionEvent.target_distance)}
            </div>
            <div className="text-xs text-gray-400">מרחק</div>
          </div>
        )}
      </div>

      {/* Timestamp */}
      {visionEvent?.captured_at && (
        <div className="text-center text-xs text-gray-400">
          עודכן: {capturedLabel(visionEvent.captured_at)}
        </div>
      )}
    </div>
  );
}
